import {LocalizedStrings} from '@/localization/types';

/**
 * Retrieves the localized string representation of a subscription unit.
 * This is used to display the unit of a subscription (e.g. "day", "week", "month").
 * The localized strings are retrieved from the localization store.
 * Depending on whether the unit should be pluralized, the appropriate string is returned.
 *
 * @param unit - The unit of the subscription (e.g. "day", "week", "month").
 * @param strings - The localized strings object containing the subscription unit translations.
 * @param isPlural - Optional parameter indicating whether the unit should be pluralized.
 * @returns The localized string representation of the subscription unit.
 * @example
 * const unit = 'day';
 * const strings = getLocalizedStrings();
 *
 * const unitString = subscriptionUnit(unit, strings);
 * console.log(unitString); // 'day'
 */
export function subscriptionUnit(
  unit: string,
  strings: LocalizedStrings,
  isPlural = false
): string {
  const units = {
    day: strings.subscriptionUnitDay ?? 'day',
    week: strings.subscriptionUnitWeek ?? 'week',
    month: strings.subscriptionUnitMonth ?? 'month',
  };

  const unitsPlural = {
    day: strings.subscriptionUnitDayPlural ?? 'days',
    week: strings.subscriptionUnitWeekPlural ?? 'weeks',
    month: strings.subscriptionUnitMonthPlural ?? 'months',
  };

  if (isPlural) {
    return unitsPlural[unit.toLowerCase() as keyof typeof unitsPlural] ?? unit;
  }
  return units[unit.toLowerCase() as keyof typeof units] ?? unit;
}
